import { useLayoutEffect, useRef, useState } from "react";

import { Icon } from "@perceptron/ui/components/icon";
import { cn } from "@perceptron/utils/cn";

import type { ToolCallLinkEntry } from "@demo/server/api/contracts/toolCall";
import { strings } from "@demo/utils/strings";

import { rowEntrance } from "./rowEntrance";

export interface ImageTileStripProps {
  results: ToolCallLinkEntry[];
  /** While the search runs the strip shows generic placeholder tiles, not thumbnails. */
  isStreaming: boolean;
}

/** Tiles shown while an image-returning search is still running; the designs use generic placeholders. */
const PLACEHOLDER_TILE_COUNT = 4;

const TILE_CLASS = "size-[117px] shrink-0 overflow-hidden rounded-lg border border-white/5";

/**
 * A generic tile; shimmers while the search that will fill it is still running. `index` staggers the shimmer
 * so it travels along the strip.
 */
export const PlaceholderTile = ({ isStreaming = false, index = 0 }: { isStreaming?: boolean; index?: number }) => (
  <div
    className={cn(TILE_CLASS, "flex items-center justify-center bg-gradient-to-b from-white/10 to-white/[0.03]", {
      "tool-call-tile-streaming": isStreaming,
    })}
    data-testid="image-result-placeholder"
    style={{ "--tile-index": index } as React.CSSProperties}
  >
    <Icon className="size-6 text-foreground-disabled" name="image" />
  </div>
);

const ThumbnailTile = ({ index, link, position }: ToolCallLinkEntry & { position: number }) => {
  // The thumbnails replace the placeholders together, so they cascade in along the strip.
  const entrance = rowEntrance(position, false);
  const image = link.image;
  const label = link.title ?? link.domain ?? link.url ?? strings.toolCall.untitled;
  const tile = image ? (
    // Result thumbnails are `data:` or third-party URLs; next/image cannot optimize those.
    // eslint-disable-next-line @next/next/no-img-element
    <img alt={label} className={cn(TILE_CLASS, "object-cover")} loading="lazy" src={image.url} />
  ) : (
    <PlaceholderTile />
  );
  return (
    <li
      className={cn("shrink-0", entrance.className)}
      data-index={index}
      data-testid="image-result-tile"
      style={entrance.style}
      title={label}
    >
      {link.url ? (
        <a className="relative block" href={link.url} rel="noreferrer noopener" target="_blank">
          {tile}
          {/* A result with a page shows a link badge over the tile's bottom-left corner. */}
          <span
            aria-hidden="true"
            className="absolute bottom-1.5 left-1.5 flex items-center justify-center rounded-md bg-black/80 p-1"
            data-testid="image-result-link-badge"
          >
            <Icon className="size-3 text-foreground-primary" name="link" />
          </span>
        </a>
      ) : (
        tile
      )}
    </li>
  );
};

/**
 * Whether a horizontally scrolling element has more to its right: false once it is scrolled to the end, or
 * when it does not overflow at all. Kept in step with scrolling and with the element's and its children's
 * size; `contentKey` changes when the children do, so the new ones are observed.
 */
function useHasMoreToRight(ref: React.RefObject<HTMLElement | null>, contentKey: string): boolean {
  const [hasMore, setHasMore] = useState(true);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const measure = () => setHasMore(element.scrollLeft + element.clientWidth < element.scrollWidth - 1);
    measure();
    element.addEventListener("scroll", measure, { passive: true });
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(element);
    for (const child of element.children) observer?.observe(child);
    return () => {
      element.removeEventListener("scroll", measure);
      observer?.disconnect();
    };
  }, [ref, contentKey]);
  return hasMore;
}

/**
 * A horizontal strip of square result tiles that fades out at the right edge, shared by the
 * image-returning searches: placeholder tiles while running, thumbnails once the results are in,
 * each with a link badge when the result has a page.
 */

function ImageTileStrip({ results, isStreaming }: ImageTileStripProps) {
  const stripRef = useRef<HTMLUListElement>(null);
  // Placeholders give way to thumbnails, and results arrive: either changes the tiles to observe.
  const hasMoreToRight = useHasMoreToRight(stripRef, `${isStreaming}:${results.length}`);
  return (
    <div className="relative w-full">
      <ul
        className="flex w-full gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        data-testid="image-results-strip"
        ref={stripRef}
      >
        {isStreaming
          ? Array.from({ length: PLACEHOLDER_TILE_COUNT }, (_, i) => (
              <li className="shrink-0" key={i}>
                <PlaceholderTile index={i} isStreaming />
              </li>
            ))
          : results.map(({ index, link }, position) => (
              <ThumbnailTile index={index} key={index} link={link} position={position} />
            ))}
      </ul>
      {/* Scroll indicator: the strip fades into the panel at its right edge, and the fade goes once there is
          nothing more to scroll to. */}
      <div
        className={cn(
          "pointer-events-none absolute inset-y-0 right-0 w-[35px] bg-gradient-to-l from-black to-transparent",
          "transition-opacity duration-200 ease-out motion-reduce:transition-none",
          hasMoreToRight ? "opacity-100" : "opacity-0",
        )}
        data-testid="image-results-fade"
      />
    </div>
  );
}

export default ImageTileStrip;
